import { CancelablePromise } from "./cancelablePromise";

const toUint8ArrayFn = () => {
    if (window.TextEncoder) {
        const encoder = new TextEncoder();
        return str => encoder.encode(str);
    }

    // polyfill for IE (see https://developer.mozilla.org/en-US/docs/Web/API/TextEncoder)
    // It's simplified version UTF16 symbols after 0xD800 won't be full encoded because it's rare case when smily face will be in database name
    return (str: string) => {
        let len = str.length, resPos = -1;
        let resArr = new Uint8Array(len * 3);
        for (let point = 0, i = 0; i !== len;) {
            point = str.charCodeAt(i);
            i += 1;

            if (point <= 0x007f) {
                resArr[resPos += 1] = 0 | point;
            } else if (point <= 0x07ff) {
                resArr[resPos += 1] = 192 | (point >>> 6);
                resArr[resPos += 1] = 128  | (point & 0x3f);
            } else {
                resArr[resPos += 1] = 224 | (point >>> 12);
                resArr[resPos += 1] = 128 | ((point >>> 6) & 0x3f);
                resArr[resPos += 1] = 128 | (point & 0x3f);
            }
        }

        return resArr.subarray(0, resPos + 1);
    };
};

const encodeText = toUint8ArrayFn();
const resultToString = (str: ArrayBuffer) => {
    let res = new Uint8Array(str);
    let resStr = "";

    for (let i = 0; i < res.length; ++i) {
        let code = res[i].toString(16);
        resStr += code.length === 1 ? "0" + code : code;
    }

    return resStr;
};

const generateId = () => "id" + Math.random().toString().substr(2);

/*
    It's random value that will be generated in very rare cases for old browsers
 */
const fallbackDigest = _ => CancelablePromise.resolve(generateId() + generateId());

const calcDigestFn = (): undefined | ((s: Uint8Array) => CancelablePromise<string>) => {
    // modern browsers Chrome, Firefox, Edge
    if (window.crypto && window.crypto.subtle && typeof crypto.subtle.digest === "function") {
        return (arr: Uint8Array) => {
            return CancelablePromise.resolve(
                crypto.subtle.digest("SHA-256", arr)
                    .then(resultToString)
            );
        };
    }

    // IE11 with msCrypto
    if (window.msCrypto && window.msCrypto.subtle && typeof window.msCrypto.subtle.digest === "function") {
        return (arr: Uint8Array) => {
            return new CancelablePromise((resolve, reject) => {
                let eve = window.msCrypto.subtle.digest({ name: "SHA-256" }, arr);
                eve.oncomplete = () => { resolve(resultToString(eve.result)); };
                eve.onerror = e => { reject(e); };
            });
        };
    }

    // IE10 and fall back in case of error
    return undefined;
};

export const createSha2Digest = (str: string) => {
    const calcDigest = calcDigestFn();
    if (calcDigest) {
        return calcDigest(encodeText(str)).catch(() => fallbackDigest(str));
    }

    return fallbackDigest(str);
};

export const isHashingSupported = () => calcDigestFn() !== undefined;